/**
 * "Finish ad": a post-step on a finished AI video ad (tool id song-promo-ad)
 * that makes the clip ready to post. It burns in captions of what is said in
 * the clip and a hook headline at the top, appends an end card (the ad's own
 * product photo, a call to action and an optional link or handle) and can
 * add an original jingle, and returns a NEW MP4.
 *
 * Oct 11 2026. Zeely and Topview ship ads with captions and a CTA; the ad
 * page said Solmi adds neither. A finished ad is its own creative tool job
 * (tool id "ad-finish", one engine "ad-finish") that points at the source
 * job's video, so history, downloads, the active-job limit, refunds and the
 * account-deletion sweep all reuse the creative-tool plumbing. The work runs
 * on our own server with ffmpeg (server/creativeAdFinishJob.ts), not at a
 * provider.
 */
import type { CreativeTool } from "./creativeTools";

export const AD_FINISH_TOOL_ID = "ad-finish";
/** The single "engine" a finish job records (creative_tool_jobs.model). */
export const AD_FINISH_MODEL = "ad-finish";
/** Only AI video ad results can be finished. */
export const AD_FINISH_SOURCE_TOOL_ID = "song-promo-ad";

/**
 * Credits for one finish (captions and/or end card). Our cost is a Whisper
 * read of at most 15 s of audio (whisper-1 at $0.006 a minute: ~$0.0015) and
 * 10-20 s of ffmpeg on the web replica. 2 credits (~$0.035 at a typical
 * $0.0175 a credit) keeps it near free while still charging, so the job goes
 * through the same reserve/refund path as every other creative job.
 * Decision for Kitty: free, or keep 2.
 */
export const AD_FINISH_CREDITS = 2;

/** Seconds the end card adds to the clip. */
export const AD_FINISH_END_CARD_SECONDS = 2;
/** The end card's button text (one short line). */
export const AD_FINISH_CTA_MAX_CHARS = 28;
/** The end card's link or handle, under the button. */
export const AD_FINISH_LINK_MAX_CHARS = 40;

/**
 * The hook headline (Oct 26 2026, from Zeely's video ads): a short line of
 * text in a white box at the top of the ad for its first seconds, the
 * TikTok-style hook that stops the scroll before anyone hears a word. Two
 * lines at most on a vertical ad.
 */
export const AD_FINISH_HEADLINE_MAX_CHARS = 60;
/** How long the headline stays up from the first frame. */
export const AD_FINISH_HEADLINE_SECONDS = 3;

/**
 * The job's tool entry, so a finished ad lists, names and plays like any
 * other creative result (findCreativeToolForJob). Not in creativeTools: it has
 * no page, and the generic create endpoint refuses it.
 */
export const adFinishTool: CreativeTool = {
  id: AD_FINISH_TOOL_ID,
  name: "Finished ad",
  description: "An AI video ad with captions, an end card or a jingle.",
  category: "video",
  fields: [],
  models: [AD_FINISH_MODEL],
};

/**
 * A finished ad's note when captions were asked for but nobody speaks in the
 * clip (stored in the completed job's error column; the page shows it
 * translated).
 */
export const AD_FINISH_NO_SPEECH_NOTE =
  "No speech was found in this ad, so it has no captions.";

/**
 * The sound of a finished ad: the clip's own ("keep"), an original
 * instrumental jingle under it that ducks while someone speaks ("under"), or
 * the jingle instead of it ("replace"). The jingle is one song from Solmi's
 * music pipeline (MiniMax, server/creativeAdJingle.ts), cut to the ad's
 * length with a fade-out.
 */
export const AD_FINISH_MUSIC_MODES = ["keep", "under", "replace"] as const;
export type AdFinishMusic = (typeof AD_FINISH_MUSIC_MODES)[number];

/**
 * Extra credits for a jingle: the price of one song on Solmi
 * (server/credits.ts CREDIT_COSTS.MUSIC_GENERATION, 5; a test keeps them
 * equal). MiniMax charges us ~$0.03 a song.
 */
export const AD_FINISH_JINGLE_CREDITS = 5;
/** Brand or product name the jingle is written for (optional). */
export const AD_FINISH_BRAND_MAX_CHARS = 40;

export type AdFinishOptions = {
  captions: boolean;
  endCard: { cta: string; link: string } | null;
  music?: AdFinishMusic;
  /** The hook headline's text (empty or absent: none). */
  headline?: string;
};

export function adFinishCost(options: AdFinishOptions): number {
  return (
    AD_FINISH_CREDITS +
    (options.music && options.music !== "keep" ? AD_FINISH_JINGLE_CREDITS : 0)
  );
}

// Emoji and the joiners/selectors that build them. libass draws no color
// emoji and the server bundles no emoji font, so they would burn in as empty
// boxes; they are left out of burned text instead. The ranges are the emoji
// blocks (pictographs, flags, skin tones), the symbol and dingbat blocks that
// hold ✨ ❤ ✅ ⭐, keycaps, tags and variation selectors. ZWJ and ZWNJ
// stay (Indic and Persian text needs them; libass draws them as nothing). ©, ® and ™
// stay: the caption fonts draw them. Built with RegExp: the tsconfig has no
// target (ES5), which refuses a `u` regex literal.
const EMOJI_RE = new RegExp(
  String.raw`[\u{1F000}-\u{1FAFF}\u2600-\u27BF\u2B00-\u2BFF\u{E0020}-\u{E007F}\uFE0E\uFE0F\u20E3]`,
  "gu",
);
// C0/C1 controls (newlines are folded to spaces before this), bidi
// overrides and zero-width characters a paste can carry.
const INVISIBLE_RE =
  /[\u0000-\u001F\u007F-\u009F\u200B\u200E\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\uFEFF]/g;

/**
 * User text as it will be burned in: one line, no emoji or invisible
 * characters, single spaces, at most `maxChars` characters (counted as code
 * points, so CJK and accented text are cut fairly). The client shows the
 * same cleaned value it sends.
 */
export function cleanAdFinishText(value: string, maxChars: number): string {
  const cleaned = value
    .replace(/[\r\n\t]+/g, " ")
    .replace(EMOJI_RE, "")
    .replace(INVISIBLE_RE, "")
    .replace(/\s+/g, " ")
    .trim();
  return Array.from(cleaned).slice(0, maxChars).join("").trim();
}
